import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Dashboard from './pages/Dashboard';
import HomeHub from './pages/HomeHub';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProductDetail from './pages/ProductDetail';
import DepartmentView from './pages/DepartmentView';
import GroupView from './pages/GroupView';
import Settings from './pages/Settings';
import CategoryReport from './pages/CategoryReport';
import DeletedProducts from './pages/DeletedProducts';
import ItemMaster from './pages/ItemMaster';
import ReportsHub from './pages/ReportsHub';
import MasterData from './pages/MasterData';
import SpecHub from './pages/SpecHub';
import GlobalSearch from './components/GlobalSearch';
import { initShortcuts } from './shortcuts';
import FlavorLab from './pages/FlavorLab';
import DepartmentCardReport from './pages/DepartmentCardReport';
import TotalMaterialReport from './pages/TotalMaterialReport';
import PhotoTrialUpload from './pages/PhotoTrialUpload';
import SpecSheets from './pages/SpecSheets';
import Products from './pages/Products';
import SampleTracking from './pages/SampleTracking';
import Trials from './pages/Trials';
import TastePanel from './pages/TastePanel';
import TastePanelSession from './pages/TastePanelSession';
import ConsumerReview from './pages/ConsumerReview';
import PanelistPortal from './pages/PanelistPortal';
import Approvals from './pages/Approvals';
import Login from './pages/Login';
import './App.css';

function Sidebar({ user, onLogout }) {
  const [showChangePwd, setShowChangePwd] = React.useState(false);
  const [pendingCount, setPendingCount] = React.useState(0);
  const isApprover = user?.role === 'approver';
  const isField = user?.role === 'field';
  const isSpecOnly = ['qc', 'qc_manager', 'op_manager', 'spec_viewer'].includes(user?.role);

  React.useEffect(() => {
    if (isApprover) {
      const fetchPending = () => {
        import('axios').then(m => m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' })
          .get('/approvals/pending')
          .then(r => setPendingCount(r.data.total))
          .catch(() => {}));
      };
      fetchPending();
      const interval = setInterval(fetchPending, 30000);
      return () => clearInterval(interval);
    }
  }, [isApprover]);
  const [pwdForm, setPwdForm] = React.useState({ current: '', newpwd: '', confirm: '' });

  const changePassword = async () => {
    if (!pwdForm.current || !pwdForm.newpwd) return;
    if (pwdForm.newpwd !== pwdForm.confirm) { alert('New passwords do not match'); return; }
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      // Verify current password first
      await API.post('/auth/login', { username: user.username, password: pwdForm.current });
      await API.put(`/users/${user.id}`, { password: pwdForm.newpwd, name: user.name, active: 1 });
      alert('Password changed successfully!');
      setShowChangePwd(false);
      setPwdForm({ current: '', newpwd: '', confirm: '' });
    } catch(e) {
      alert(e.response?.data?.error === 'Invalid username or password' ? 'Current password is incorrect' : 'Failed to change password');
    }
  };

  return (
    <aside className="sidebar">
      <NavLink to="/" className="sidebar-brand" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
        <div className="brand-icon">BGH</div>
        <span>NPD Tracker</span>
      </NavLink>
      <div onClick={() => window.dispatchEvent(new CustomEvent('open-global-search'))}
        style={{ margin: '10px 14px 4px', padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border, #ddd)',
                 background: 'var(--bg-hover, rgba(255,255,255,0.06))', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                 fontSize: 13, color: 'var(--text-muted, #999)' }}>
        <span>🔍</span>
        <span>Search...</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, border: '1px solid var(--border, #ccc)', borderRadius: 5, padding: '1px 5px' }}>Ctrl K</span>
      </div>
      <nav className="sidebar-nav">
        {!isSpecOnly && <>
        <div className="nav-section">Evaluation</div>
        <NavLink to="/trials" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>Trials</NavLink>
        <NavLink to="/taste-panel" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>Tasting Folks</NavLink>
        <NavLink to="/photo-upload" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Photo Trial
        </NavLink>
        </>}
        {isApprover && !isSpecOnly && (
          <NavLink to="/approvals" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
            Approvals
            {pendingCount > 0 && <span style={{ marginLeft: 'auto', background: 'var(--red)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{pendingCount}</span>}
          </NavLink>
        )}
        {isSpecOnly && <>
        <div className="nav-section">System</div>
        <NavLink to="/specs" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Spec Sheets
        </NavLink>
        </>}
        {!isField && !isSpecOnly && <>
        <div className="nav-section">Reports</div>
        <NavLink to="/reports" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Reports
        </NavLink>
        <div className="nav-section">System</div>
        <NavLink to="/specs" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Spec Sheets
        </NavLink>
        <NavLink to="/master-data" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Master Data
        </NavLink>
        <NavLink to="/flavor-lab" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Flavor Lab
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
          Settings
        </NavLink></>}
        <div className="mobile-only-nav">
          <button className="nav-item" onClick={() => setShowChangePwd(true)}>
            Password
          </button>
          <button className="nav-item" onClick={onLogout}>
            Logout
          </button>
        </div>
      </nav>
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-name">{user?.name}</div>
          <div className="user-username">{user?.username}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowChangePwd(true)} style={{ width: '100%', justifyContent: 'center', fontSize: 11, marginBottom: 4 }}>
          🔑 Change Password
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onLogout} style={{ width: '100%', justifyContent: 'center', fontSize: 11 }}>
          Sign Out
        </button>
        {showChangePwd && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 340 }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Change Password</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Current Password</label><input type="password" value={pwdForm.current} onChange={e => setPwdForm({...pwdForm, current: e.target.value})} style={{ width: '100%', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text-primary)', fontSize: 13 }} /></div>
                <div><label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>New Password</label><input type="password" value={pwdForm.newpwd} onChange={e => setPwdForm({...pwdForm, newpwd: e.target.value})} style={{ width: '100%', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text-primary)', fontSize: 13 }} /></div>
                <div><label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Confirm New Password</label><input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} style={{ width: '100%', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text-primary)', fontSize: 13 }} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowChangePwd(false); setPwdForm({ current: '', newpwd: '', confirm: '' }); }} style={{ padding: '7px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                <button onClick={changePassword} style={{ padding: '7px 14px', background: 'var(--accent)', border: 'none', borderRadius: 6, color: 'var(--bg-base)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Change Password</button>
              </div>
            </div>
          </div>
        )}
        <div className="version-tag" style={{ marginTop: 8 }}>v1.2</div>
      </div>
    </aside>
  );
}

function isSpecOnlyUser(u) { return ['qc','qc_manager','op_manager','spec_viewer'].includes(u?.role); }
export default
function App() {
  const [showGlobalSearch, setShowGlobalSearch] = React.useState(false);
  React.useEffect(() => initShortcuts(() => setShowGlobalSearch(true)), []);
  React.useEffect(() => {
    const open = () => setShowGlobalSearch(true);
    window.addEventListener('open-global-search', open);
    return () => window.removeEventListener('open-global-search', open);
  }, []);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('npd_user');
    if (saved) { try { setUser(JSON.parse(saved)); } catch {} }
  }, []);

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => { localStorage.removeItem('npd_user'); setUser(null); };

  // Public routes - no auth needed
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/panel')) {
    return (
      <BrowserRouter>
      {showGlobalSearch && <GlobalSearch onClose={() => setShowGlobalSearch(false)} />}
        <Routes>
          <Route path="/panel" element={<PanelistPortal />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    );
  }

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/review/')) {
    return (
      <BrowserRouter>
      {showGlobalSearch && <GlobalSearch onClose={() => setShowGlobalSearch(false)} />}
        <Routes>
          <Route path="/review/:session_code" element={<ConsumerReview />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    );
  }

  if (!user) return (
    <>
      <Login onLogin={handleLogin} />
      <Toaster position="top-right" toastOptions={{ style: { background: '#1a1f2e', color: '#e2e8f0', border: '1px solid #2d3748' } }} />
    </>
  );

  return (
    <BrowserRouter>
      {showGlobalSearch && <GlobalSearch onClose={() => setShowGlobalSearch(false)} />}
      <div className="app-layout">
        <Sidebar user={user} onLogout={handleLogout} />
        <main className="main-content">
          <Routes>
            <Route path="/photo-upload" element={<PhotoTrialUpload user={user} />} />
                <Route path="/products-list" element={<Products />} />
                <Route path="/item-master" element={<ItemMaster />} />
                <Route path="/reports" element={<ReportsHub />} />
                <Route path="/master-data" element={<MasterData />} />
                <Route path="/specs" element={<SpecHub />} />
                <Route path="/flavor-lab" element={<FlavorLab />} />
                <Route path="/deleted-products" element={<DeletedProducts />} />
                <Route path="/taste-panel" element={<TastePanel />} />
                <Route path="/taste-panel/:id" element={<TastePanelSession />} />
                <Route path="/review/:session_code" element={<ConsumerReview />} />
                <Route path="/trials" element={<Trials />} />
                <Route path="/samples" element={<SampleTracking />} />
                <Route path="/spec-sheets" element={<SpecSheets />} />
            {user?.role === 'field' ? (
              <Route path="*" element={<Navigate to="/photo-upload" replace />} />
            ) : (
              <>
                <Route path="/" element={isSpecOnlyUser(user) ? <Navigate to="/specs" replace /> : <HomeHub />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/by-department" element={<DepartmentView />} />
                <Route path="/by-group" element={<GroupView />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/category-report" element={<CategoryReport />} />
                <Route path="/dept-card-report" element={<DepartmentCardReport />} />
                <Route path="/approvals" element={<Approvals />} />
                <Route path="/material-report" element={<TotalMaterialReport />} />
              </>
            )}
          </Routes>
        </main>
      </div>
      <Toaster position="top-right" toastOptions={{ style: { background: '#1a1f2e', color: '#e2e8f0', border: '1px solid #2d3748' } }} />
    </BrowserRouter>
  );
}
